import { FC } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as Lightning } from 'assets/icons/dashboard-page-icons/lightning.svg';
import { NBSP_JS } from 'constants/unicode/nbsp-js';
import dayjs from 'dayjs';
import de from 'dayjs/locale/de';
import en from 'dayjs/locale/en';
import es from 'dayjs/locale/es';
import it from 'dayjs/locale/it';
import ru from 'dayjs/locale/ru';
import { useInnerWidth } from 'hooks/useInnerWidth';
import { selectIsOnlyTrialMinerInAssets } from 'store/assets-reducer/assets.selectors';
import { selectUptime } from 'store/uptime-reducer/uptime.selectors';
import { BreakPoint } from 'styles/style-variables/breakpoint';

import { useLanguage } from 'components/logic/LanguageProvider';
import { LANGUAGES } from 'components/logic/LanguageProvider/types/language-provider.interface';
import { Tooltip } from 'components/ui/Tooltip';
import { formatToNumberWithSpaces } from 'utils/formatters/format-to-number-with-spaces.util';

import * as S from './ElectricityArticle.styled';

interface IElectricityArticleProps {
  paymentBalanceUsd: string;
  paymentUsdDay: string;
  dailyPaymentUsd: string;
  perKwValue: string;
  availableDays: number;
  handleReplenishClick: () => void;
  t: (key: string) => string;
}

export const ElectricityArticle: FC<IElectricityArticleProps> = ({
  paymentBalanceUsd,
  paymentUsdDay,
  dailyPaymentUsd,
  perKwValue,
  availableDays,
  handleReplenishClick,
  t,
}) => {
  const isOnlyTrialMinersInAssets = useSelector(selectIsOnlyTrialMinerInAssets);
  const { innerWidth } = useInnerWidth();
  const { uptime } = useSelector(selectUptime);
  const { language } = useLanguage();

  const getDayJsLocale = () => {
    switch (language) {
      case LANGUAGES.DEU:
        return de;
      case LANGUAGES.ITA:
        return it;
      case LANGUAGES.ESP:
        return es;
      case LANGUAGES.RUS:
        return ru;
      default:
        return en;
    }
  };

  const paidDays = `${formatToNumberWithSpaces(availableDays)} ${t('wallet.days')}`;
  const currentDate = dayjs().utc();
  const untilDate = dayjs(currentDate)
    .add(availableDays, 'days')
    .utc()
    .locale(getDayJsLocale())
    .format(`${t('global.until')} MMM. DD.YYYY`);

  const isPaidDaysMoreThanOne = availableDays > 1;

  const isLessThanTablet = innerWidth <= parseInt(BreakPoint.TabletTop);

  return (
    <S.ElectricityArticle>
      <S.BalanceWrapper>
        <S.BalanceTitle>
          <S.ElectricityIcon>
            <Lightning />
          </S.ElectricityIcon>
          {t('wallet.electricityBalance')}
        </S.BalanceTitle>
        <S.Container>
          <S.BalanceValue>
            {paymentBalanceUsd}
            {!isOnlyTrialMinersInAssets && isLessThanTablet && isPaidDaysMoreThanOne && (
              <S.SufficientValue as="div">
                {uptime && '≈' + NBSP_JS}
                {paidDays}
                {uptime && (
                  <>
                    {NBSP_JS}
                    <S.UntilDateValue>{untilDate}</S.UntilDateValue>
                  </>
                )}
              </S.SufficientValue>
            )}
          </S.BalanceValue>
          {uptime && !isOnlyTrialMinersInAssets && isLessThanTablet && isPaidDaysMoreThanOne && (
            <Tooltip tooltipContent={t('wallet.estimatedDurationNote')} />
          )}
        </S.Container>
      </S.BalanceWrapper>
      <S.ReplenishButtonWrapper>
        <S.ReplenishButton text={t('wallet.deposit')} onClick={handleReplenishClick} />
      </S.ReplenishButtonWrapper>
      <S.Separator1 />
      <S.DailyPaymentWrapper>
        <S.PaymentTitle>
          {uptime
            ? t('wallet.electricityAvgDailyPaymentTitle')
            : t('wallet.electricityDailyPaymentTitle')}
          {uptime && <Tooltip tooltipContent={t('wallet.dailyElectricity65UptimeNote')} />}
        </S.PaymentTitle>
        <S.PaymentValue>
          {isOnlyTrialMinersInAssets ? '$0' : dailyPaymentUsd}
          <S.PerKwValue>
            {NBSP_JS}
            {perKwValue}
          </S.PerKwValue>
        </S.PaymentValue>
      </S.DailyPaymentWrapper>
      <S.Separator2 />
      {!isOnlyTrialMinersInAssets && isPaidDaysMoreThanOne && (
        <S.SufficientForWrapper>
          <S.SufficientTitle>
            {t('wallet.electricitySufficientForTitle')}
            {uptime && <Tooltip tooltipContent={t('wallet.estimatedDurationNote')} />}
          </S.SufficientTitle>
          <S.SufficientValue>
            {uptime && '≈'}
            {paidDays}
            {uptime && (
              <>
                {NBSP_JS}
                <S.UntilDateValue>{untilDate}</S.UntilDateValue>
              </>
            )}
          </S.SufficientValue>
          <S.SufficientValueUsdDay>{paymentUsdDay}</S.SufficientValueUsdDay>
        </S.SufficientForWrapper>
      )}
    </S.ElectricityArticle>
  );
};
